import { convertToDarkerRgba } from '@hivebrite/design-system/Theme';

/**
 * List styles that allow supporting both these features at the same time:
 * 1. Left-aligned list items have their first line indentation the same as their other lines
 * 2. List markers follow the text (as opposed to staying on the left) when a list item is aligned center or right
 */
export const listStyles = () => `
  ul,
  ol {
    padding-left: 0;
    counter-reset: counter;
    margin-left: 40px;
    margin-block-start: 1em;
    margin-block-end: 1em;
  }

  ul li,
  ol li {
    list-style: none;
    counter-increment: counter;
    position: relative;
  }

  ul li[style*='text-align: center']::before,
  ul li[style*='text-align: right']::before,
  ol li[style*='text-align: center']::before,
  ol li[style*='text-align: right']::before {
    position: relative;
    left: -0.5em;
  }

  ul li::before,
  ol li::before {
    position: absolute;
    right: calc(100% + 0.5em);
    text-wrap: nowrap;
  }

  ul li::before {
    content: counter(counter, disc) ' ';
  }

  ol li::before {
    content: counter(counter) '. ';
  }

  ul[style*='list-style-type: circle'] > li::before {
    content: counter(counter, circle) ' ';
  }

  ul[style*='list-style-type: square'] > li::before {
    content: counter(counter, square) ' ';
    font-family: sans-serif;
  }

  ol[style*='list-style-type: lower-roman'] > li::before {
    content: counter(counter, lower-roman) '. ';
  }

  ol[style*='list-style-type: lower-alpha'] > li::before {
    content: counter(counter, lower-alpha) '. ';
  }

  ol[style*='list-style-type: lower-greek'] > li::before {
    content: counter(counter, lower-greek) '. ';
  }

  ol[style*='list-style-type: upper-roman'] > li::before {
    content: counter(counter, upper-roman) '. ';
  }

  ol[style*='list-style-type: upper-alpha'] > li::before {
    content: counter(counter, upper-alpha) '. ';
  }
`;

const linkStyles = ({ colors, fontWeights }) => `
  a:not(.button),
  a:not(.button) span {
    color: ${colors.link};
    text-decoration: underline;
  }

  a:not(.button):hover,
  a:not(.button):hover span {
    color: ${convertToDarkerRgba(colors.link)};
  }

  strong a:not(.button),
  strong a:not(.button) span {
    font-weight: ${fontWeights[1]};
    text-decoration-thickness: 2px;
  }
`;

const imageAndFigureStyles = () => `
  img {
    display: inline-block;
    max-width: 100%;
    object-fit: contain;
    object-position: top;
    margin-right: 16px;
    vertical-align: baseline;
    height: unset;
  }

  img[style*='float: right'] {
    margin-left: 16px;
    margin-right: 0;
  }

  .img-left { float: left; }
  .img-right { float: right; }
  .img-center {
    float: none;
    margin: 0;
    text-align: center;
  }

  figure {
    margin: 0 16px 0 0;
    float: left;
    display: inline-block;
  }
  figure img { margin: 0; }
  figure.align-center {
    margin: 0 auto;
    float: none;
    display: table;
  }

  figcaption {
    text-align: center;
    line-height: 2em;
    min-width: auto;
  }
`;

// Based off the defaultTheme values for h#-regular fonts in the front-monorepo
// See packages/design-system/theme/__tests__/defaultTheme.js in the front-monorepo for the values
const headingStyles = ({
  breakpoints,
  colors,
  fontSizes,
  fontWeights,
  lineHeights,
}) => `
  h1, h2, h3, h4, h5, h6 {
    color: ${colors.black};
    font-weight: ${fontWeights[0]};
  }

  h1{
    font-size: ${fontSizes[8]};
    line-height: ${lineHeights[8]};
    margin-bottom: 2.1rem;
  }

  h2 {
    font-size: ${fontSizes[6]};
    line-height: ${lineHeights[6]};
    margin-bottom: 1.5rem;
  }

  h3 {
    font-size: ${fontSizes[4]};
    line-height: ${lineHeights[4]};
    margin-bottom: 1.4rem;
  }

  h4 {
    font-size: ${fontSizes[3]};
    line-height: ${lineHeights[3]};
    margin-bottom: 1.3rem;
  }

  h5 {
    font-size: ${fontSizes[2]};
    line-height: ${lineHeights[2]};
    margin-bottom: 1.2rem;
  }

  h6 {
    font-size: ${fontSizes[1]};
    line-height: ${lineHeights[1]};
    margin-bottom: 1.1rem;
  }

  @media (min-width: ${breakpoints.l}) {
    h1 {
      font-size: ${fontSizes[9]};
      line-height: ${lineHeights[9]};
    }

    h2 {
      font-size: ${fontSizes[7]};
      line-height: ${lineHeights[7]};
    }

    h3 {
      font-size: ${fontSizes[5]};
      line-height: ${lineHeights[5]};
    }

    h4 {
      font-size: ${fontSizes[3]};
      line-height: ${lineHeights[3]};
    }

    h5 {
      font-size: ${fontSizes[2]};
      line-height: ${lineHeights[2]};
    }

    h6 {
      font-size: ${fontSizes[1]};
      line-height: ${lineHeights[1]};
    }
  }
`;

const editorStyles = () => `
  .mce-content-body [data-mce-selected='inline-boundary'] {
    outline: 1px solid #b4d7ff;
    outline-offset: 0;
    background-color: transparent;
  }

  blockquote {
    display: block;
    font-style: italic;
    font-family: Georgia, Times, 'Times New Roman', serif;
    padding: 2px 0;
    border-style: solid;
    border-color: #ccc;
    border-width: 0;
    margin: 1em 1.5em;
    padding-left: 20px;
    padding-right: 8px;
    border-left-width: 2px;
  }

  blockquote p {
    margin: 1em 0;
  }
`;

const textStyles = ({ colors, fonts, fontSizes, fontWeights }) => `

  p {
    margin-bottom: 20px;
  }

  span, a {
    line-height: 1.215;
  }

  p, li, td, figcaption {
    color: ${colors[fonts['body-regular-asphalt'].color]};
    font-size: ${fontSizes[fonts['body-regular-asphalt'].fontSize]};
    font-weight: ${fontWeights[fonts['body-regular-asphalt'].fontWeight]};
    line-height: 1.215;
  }

  strong, b { font-weight: ${fontWeights[1]}; }

  i, cite, em, var, address, dfn { font-style: italic; }
  u, ins { text-decoration: underline; }
  s, strike, del { text-decoration: line-through; }

  sub { vertical-align: sub; }
  sup { vertical-align: super; }
  sub, sup { font-size: smaller; }

  tt, code, kbd, samp { font-family: monospace; }

  q { quotes: initial; }
  q::before { content: open-quote; }
  q::after { content: close-quote; }

  big { font-size: larger; }
  small { font-size: smaller; }

  .marker { background-color: Yellow; }
`;

const tableStyles = ({ fontWeights }) => `
  table {
    border-collapse: collapse;
    width: 100%;
  }

  table td,
  table th {
    vertical-align: top;
    padding: 0.4rem;
  }

  table thead td,
  table th {
    font-weight: ${fontWeights[1]};
  }

  table[border]:not([border="0"]):not([style*=border-color]) td, table[border]:not([border="0"]):not([style*=border-color]) th {
    border: solid 1px currentColor;
  }


  table[style*="border-style: hidden"] td,
  table[style*="border-style: hidden"] th,
  table[style*="border:"][style*="hidden"] td,
  table[style*="border:"][style*="hidden"] th {
    border-style: hidden !important;
  }

  table[style*="border-style: dotted"] td,
  table[style*="border-style: dotted"] th,
  table[style*="border:"][style*="dotted"] td,
  table[style*="border:"][style*="dotted"] th {
    border-style: dotted !important;
  }

  table[style*="border-style: dashed"] td,
  table[style*="border-style: dashed"] th,
  table[style*="border:"][style*="dashed"] td,
  table[style*="border:"][style*="dashed"] th {
    border-style: dashed !important;
  }

  table[style*="border-style: solid"] td,
  table[style*="border-style: solid"] th,
  table[style*="border:"][style*="solid"] td,
  table[style*="border:"][style*="solid"] th {
    border-style: solid !important;
  }

  table[style*="border-style: double"] td,
  table[style*="border-style: double"] th,
  table[style*="border:"][style*="double"] td,
  table[style*="border:"][style*="double"] th {
    border-style: double !important;
  }

  table[style*="border-style: groove"] td,
  table[style*="border-style: groove"] th,
  table[style*="border:"][style*="groove"] td,
  table[style*="border:"][style*="groove"] th {
    border-style: groove !important;
  }

  table[style*="border-style: ridge"] td,
  table[style*="border-style: ridge"] th,
  table[style*="border:"][style*="ridge"] td,
  table[style*="border:"][style*="ridge"] th {
    border-style: ridge !important;
  }

  table[style*="border-style: inset"] td,
  table[style*="border-style: inset"] th,
  table[style*="border:"][style*="inset"] td,
  table[style*="border:"][style*="inset"] th {
    border-style: inset !important;
  }

  table[style*="border-style: outset"] td,
  table[style*="border-style: outset"] th,
  table[style*="border:"][style*="outset"] td,
  table[style*="border:"][style*="outset"] th {
    border-style: outset !important;
  }

  table[border='0'],
  table[border='0'] td,
  table[border='0'] th {
    border: none;
  }

  table td img,
  table th img{
    height: unset;
  }
`;

const miscStyles = () => `
  iframe {
    max-width: 100%;
    height: unset;
  }

  hr {
    border-color: #ccc;
  }

  .embedly-embed {
    max-width: 100%;
    height: unset;
  }
`;

const emailTextStyle = () => `
  p {
    margin-bottom: 10px;
  }

  blockquote {
    display: block;
    font-style: italic;
    font-family: Georgia, Times, 'Times New Roman', serif;
    border-style: solid;
    border-color: #ccc;
    border-width: 0;
    margin: 1em 40px;
    padding-left: 20px;
    padding-right: 8px;
    border-left-width: 5px;
  }
`;

const emailTemplateLayout = () => `
  html {
    background-color: rgb(251 251 251);

    /* Mailer base font email*/
    color: #505050;
    font-family: 'Helvetica Neue', Arial;
    font-size: 14px;
  }

  .mce-content-body {

    /* Current mailer behavior:
      - Layout is centered with a default width of 600px.
      - If images are wider than 600px, the layout expands to match the image width.
    */
    min-width: 600px;
    max-width: fit-content;
    margin: 0 auto;

    background-color: white;
    padding-left: 20px;
    padding-right: 20px;
  }
`;

export const sharedStyles = (theme) => `
  ${miscStyles()}
  ${linkStyles(theme)}
  ${imageAndFigureStyles()}
  ${headingStyles(theme)}
  ${editorStyles()}
  ${textStyles(theme)}
  ${tableStyles(theme)}
  ${listStyles()}
`;

export const webStyles = (theme) => `
  ${sharedStyles(theme)}
`;

export const emailStyles = (theme) => `
  // Shared styles minus the editor styles
  ${miscStyles()}
  ${linkStyles(theme)}
  ${imageAndFigureStyles()}
  ${headingStyles(theme)}
  ${textStyles(theme)}
  ${tableStyles(theme)}
  ${listStyles()}

  // Email specific
  ${emailTemplateLayout()}
  ${emailTextStyle()}
`;
